"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    AMENDE,
    diagnostic,
    concerne,
    mentionType,
    recapitulatif,
} from "@/lib/tools/consumerMediation";
import {
    Avancer,
    Champ,
    Choix,
    Ecran,
    Liste,
    Recommencer,
    Resultat,
    Segments,
    euros,
    remplir,
    useParcours,
} from "./Journey";
import { useDossierOutil, useSignalerDossier } from "./ToolsDossier";

/**
 * Le médiateur de la consommation : êtes-vous en règle, et que manque-t-il ?
 *
 * Cet outil ne fabrique aucun document, et c'est voulu : le médiateur se
 * choisit sur la liste officielle, contre un contrat payant. Ce qu'il produit,
 * c'est un diagnostic et la MENTION à recopier, celle qui manque le plus
 * souvent chez ceux qui ont pourtant payé leur médiateur.
 */

const L = {
    fr: {
        phases: ["Vos clients", "Votre situation", "Diagnostic"],
        retour: "Retour",
        prive: "Tout se fait sur votre appareil : personne ne reçoit vos réponses, pas même nous, et tout disparaît avec l'onglet.",

        clientsTitre: "À qui vendez-vous ?",
        clientsAide: "Tout part de votre clientèle : la médiation de la consommation ne règle que les litiges entre un consommateur et un professionnel.",
        clientsParticuliers: "À des particuliers",
        clientsParticuliersDetail: "Vente, prestations, formation, artisanat",
        clientsProfessionnels: "À des entreprises seulement",
        clientsProfessionnelsDetail: "Vos factures partent à des sociétés",
        clientsDeux: "Aux deux",
        clientsDeuxDetail: "Particuliers et entreprises",

        secteurTitre: "Votre activité relève-t-elle d'un de ces domaines ?",
        secteurAide: "Trois secteurs ont leurs propres voies de recours, et la médiation de la consommation ne s'y applique pas.",
        secteurSante: "Soins par un professionnel de santé",
        secteurEnseignement: "Enseignement supérieur public",
        secteurInteretGeneral: "Service d'intérêt général non économique",
        secteurAutre: "Aucun de ces domaines",

        horsSujetTitre: "Vous n'êtes pas concerné",
        refusProfessionnels: "La médiation de la consommation ne règle que les litiges entre un consommateur et un professionnel. Tant que tous vos clients sont des entreprises, vous n'avez rien à signer ni à ajouter à vos conditions générales. Le jour où vous vendrez à un particulier, même une seule fois, l'obligation naîtra : revenez à ce moment-là.",
        refusSecteur: "Votre domaine dispose de ses propres voies de recours, et la médiation de la consommation ne s'y substitue pas. Renseignez-vous auprès de votre ordre, de votre tutelle ou de votre autorité de rattachement : c'est de ce côté que se trouve le dispositif qui vous concerne.",

        mediateurTitre: "Avez-vous désigné un médiateur ?",
        mediateurAide: "Le médiateur doit figurer sur la liste officielle des médiateurs de la consommation. L'adhésion est payante, et c'est vous qui la réglez : la médiation est gratuite pour votre client.",
        champMediateur: "Un médiateur est désigné",
        mediateurOui: "Oui",
        mediateurNon: "Non",
        mediateurSaisPas: "Je ne sais pas",
        champNomMediateur: "Nom du médiateur",
        aideNomMediateur: "Facultatif. Si vous le renseignez, nous écrivons la mention à recopier dans vos conditions générales.",
        champSiteMediateur: "Son site internet",
        champForme: "Votre entreprise est",
        formeIndividuelle: "Une entreprise individuelle",
        formeSociete: "Une société",

        mentionsTitre: "Où l'avez-vous écrit ?",
        mentionsAide: "Désigner un médiateur ne suffit pas : ses coordonnées doivent être communiquées au consommateur, de manière visible et lisible, avant même qu'un litige existe.",
        champSite: "J'ai un site internet",
        champMentionSite: "Les coordonnées du médiateur y figurent",
        champMentionConditions: "Elles figurent dans mes conditions générales ou sur mes devis",
        oui: "Oui",
        non: "Non",

        continuer: "Continuer",
        voirDiagnostic: "Voir mon diagnostic",
        resultatTitre: "Votre situation",
        recapTitre: "Ce que vous avez déclaré",
        recommencer: "Recommencer",

        enRegleTitre: "Vous êtes en règle",
        enRegleTexte: "Vous avez un médiateur désigné et ses coordonnées sont communiquées aux endroits prévus. Pensez à vérifier chaque année que votre médiateur figure toujours sur la liste officielle : un référencement retiré vous laisserait sans dispositif, sans que personne vous prévienne.",
        manquesTitre: "Il vous manque {n} chose",
        manquesTitrePluriel: "Il vous manque {n} choses",
        manquesAide: "Il suffit d'un seul manquement pour être sanctionné : vous encourez jusqu'à {amende} d'amende administrative.",
        manqueDesigner: "Désigner un médiateur inscrit sur la liste officielle, et signer avec lui.",
        manqueEcrireEnsuite: "Une fois qu'il est désigné, écrire son nom et ses coordonnées sur votre site et dans vos conditions générales.",
        manqueMentionSite: "Écrire son nom et ses coordonnées sur votre site internet, de manière visible et lisible.",
        manqueMentionConditions: "Les faire figurer dans vos conditions générales, sur vos devis ou vos bons de commande.",

        mentionTitre: "La mention à recopier",
        mentionAide: "Collez-la dans vos conditions générales et sur votre site. Elle nomme votre médiateur et rappelle la réclamation préalable, sans laquelle il n'est pas saisi.",
        copier: "Copier la mention",
        copie: "Mention copiée",

        suiteTitre: "Et maintenant",
        suite1: "Choisissez votre médiateur sur la liste officielle publiée par la commission d'évaluation et de contrôle de la médiation de la consommation : lui seul peut être opposé à un consommateur.",
        suite2: "Comptez quelques dizaines d'euros par an pour une micro-entreprise, selon le médiateur et votre volume de dossiers.",
        suite3: "Écrivez ses coordonnées à deux endroits au moins : votre site et vos conditions générales.",
        suite4: "Répondez toujours par écrit à une réclamation : le médiateur ne peut être saisi qu'après une tentative directe restée sans réponse ou refusée.",

        fondTitre: "Ce que dit la loi",
        fond: "L'article L. 612-1 du code de la consommation garantit à tout consommateur le recours gratuit à un médiateur, depuis le {depuis}. L'article L. 616-1 impose au professionnel de communiquer les coordonnées du médiateur dont il relève. Le manquement est puni d'une amende administrative pouvant atteindre {amendeIndividuelle} pour une entreprise individuelle et {amendeSociete} pour une société, quelle que soit la taille de l'entreprise.",
    },
    en: {
        phases: ["Your clients", "Your situation", "Diagnosis"],
        retour: "Back",
        prive: "Everything happens on your device: nobody receives your answers, not even us, and it all disappears with the tab.",

        clientsTitre: "Who do you sell to?",
        clientsAide: "It all starts with your clients: consumer mediation only settles disputes between a consumer and a professional.",
        clientsParticuliers: "To individuals",
        clientsParticuliersDetail: "Retail, services, training, craft work",
        clientsProfessionnels: "To companies only",
        clientsProfessionnelsDetail: "Your invoices go to businesses",
        clientsDeux: "To both",
        clientsDeuxDetail: "Individuals and companies",

        secteurTitre: "Does your activity fall into one of these fields?",
        secteurAide: "Three sectors have their own channels of redress, and consumer mediation does not apply to them.",
        secteurSante: "Care from a health professional",
        secteurEnseignement: "Public higher education",
        secteurInteretGeneral: "Non-economic service of general interest",
        secteurAutre: "None of these",

        horsSujetTitre: "This does not concern you",
        refusProfessionnels: "Consumer mediation only settles disputes between a consumer and a professional. As long as all your clients are businesses, you have nothing to sign and nothing to add to your terms and conditions. The day you sell to an individual, even once, the obligation arises: come back then.",
        refusSecteur: "Your field has its own channels of redress, and consumer mediation does not replace them. Ask your professional body or your supervising authority: that is where the scheme that concerns you sits.",

        mediateurTitre: "Have you appointed a mediator?",
        mediateurAide: "The mediator must appear on the official list of consumer mediators. Membership is paid for, and you are the one who pays: mediation is free for your client.",
        champMediateur: "A mediator is appointed",
        mediateurOui: "Yes",
        mediateurNon: "No",
        mediateurSaisPas: "I do not know",
        champNomMediateur: "Mediator's name",
        aideNomMediateur: "Optional. If you fill it in, we write the wording to copy into your terms and conditions.",
        champSiteMediateur: "Their website",
        champForme: "Your business is",
        formeIndividuelle: "A sole trader",
        formeSociete: "A company",

        mentionsTitre: "Where have you written it?",
        mentionsAide: "Appointing a mediator is not enough: their contact details must be given to the consumer, visibly and legibly, before any dispute even exists.",
        champSite: "I have a website",
        champMentionSite: "The mediator's details appear on it",
        champMentionConditions: "They appear in my terms and conditions or on my quotes",
        oui: "Yes",
        non: "No",

        continuer: "Continue",
        voirDiagnostic: "See my diagnosis",
        resultatTitre: "Where you stand",
        recapTitre: "What you told us",
        recommencer: "Start again",

        enRegleTitre: "You are compliant",
        enRegleTexte: "You have an appointed mediator and their details are given where required. Check every year that your mediator is still on the official list: a withdrawn listing would leave you with no scheme, and nobody would tell you.",
        manquesTitre: "You are missing {n} thing",
        manquesTitrePluriel: "You are missing {n} things",
        manquesAide: "A single breach is enough to be penalised: you face an administrative fine of up to {amende}.",
        manqueDesigner: "Appoint a mediator from the official list, and sign with them.",
        manqueEcrireEnsuite: "Once they are appointed, write their name and contact details on your website and in your terms and conditions.",
        manqueMentionSite: "Write their name and contact details on your website, visibly and legibly.",
        manqueMentionConditions: "Include them in your terms and conditions, on your quotes or your order forms.",

        mentionTitre: "The wording to copy",
        mentionAide: "Paste it into your terms and conditions and onto your site. It names your mediator and recalls the prior complaint, without which they cannot be approached.",
        copier: "Copy the wording",
        copie: "Wording copied",

        suiteTitre: "What happens next",
        suite1: "Choose your mediator from the official list published by the consumer mediation commission: only they can be relied on against a consumer.",
        suite2: "Budget a few tens of euros a year for a micro-business, depending on the mediator and your volume of cases.",
        suite3: "Write their details in at least two places: your website and your terms and conditions.",
        suite4: "Always answer a complaint in writing: the mediator can only be approached after a direct attempt that went unanswered or was refused.",

        fondTitre: "What the law says",
        fond: "Article L. 612-1 of the consumer code guarantees every consumer free access to a mediator, since {depuis}. Article L. 616-1 requires the professional to communicate the details of the mediator they come under. A breach is punished by an administrative fine of up to {amendeIndividuelle} for a sole trader and {amendeSociete} for a company, whatever the size of the business.",
    },
};

const PHASES_PAR_ECRAN = {
    clients: 0,
    secteur: 0,
    horsSujet: 0,
    mediateur: 1,
    mentions: 1,
    resultat: 2,
};

/* Aucune réponse n'est posée d'avance : un bouton présélectionné répond à la
   place du lecteur, et il ne le voit pas. Sur ce parcours, « entreprise
   individuelle » décidait du montant d'amende affiché, et « j'ai un site »
   ajoutait une obligation qu'il n'avait jamais reconnue. */
const VIDE = {
    clients: "",
    secteur: "",
    forme: "",
    mediateur: "",
    nomMediateur: "",
    siteMediateur: "",
    site: "",
    mentionSite: "",
    mentionConditions: "",
};

export default function MediateurConsommation({ lang = "fr", aujourdhui }) {
    const t = L[lang] || L.fr;
    // Le dossier du lecteur, s'il en a un : la forme juridique part déjà
    // posée. La première question (la clientèle) ne s'en déduit pas : le
    // parcours part de son début.
    const { valeurs: dossier } = useDossierOutil();
    const [r, setR] = useState(() => ({ ...VIDE, ...(dossier.forme ? { forme: dossier.forme } : {}) }));
    const [copie, setCopie] = useState(false);

    const { etape, sens, reduit, rang, aller, retour, recommencer, communs } = useParcours(
        "clients",
        () => {
            setR(VIDE);
            setCopie(false);
        },
        PHASES_PAR_ECRAN,
        null,
        // Reprendre où l'on en était, le temps de l'onglet (Journey.jsx).
        { r, setR },
    );

    useSignalerDossier({ forme: r.forme }, etape === "resultat");

    const poser = (cle) => (valeur) => setR((p) => ({ ...p, [cle]: valeur }));

    const etat = useMemo(() => diagnostic(r, aujourdhui), [r, aujourdhui]);
    const recap = useMemo(() => recapitulatif(r, aujourdhui), [r, aujourdhui]);
    const mention = useMemo(() => mentionType(r), [r]);
    const commun = { ...communs, phases: t.phases, retour: t.retour, rang };
    const tri = concerne(r);

    const ouiNon = [
        { cle: "oui", libelle: t.oui },
        { cle: "non", libelle: t.non },
    ];

    const copier = async () => {
        if (!mention) return;
        try {
            await navigator.clipboard.writeText(mention);
            setCopie(true);
            window.setTimeout(() => setCopie(false), 2000);
        } catch {
            // Presse-papiers refusé : le texte reste sélectionnable à l'écran.
        }
    };

    if (etape === "clients") {
        return (
            <Ecran key={etape} {...commun} phase={0} titre={t.clientsTitre} aide={t.clientsAide}>
                <div className="grid sm:grid-cols-3 gap-3">
                    {[
                        { cle: "particuliers", icone: "user-rounded-linear", titre: t.clientsParticuliers, detail: t.clientsParticuliersDetail },
                        { cle: "professionnels", icone: "buildings-2-linear", titre: t.clientsProfessionnels, detail: t.clientsProfessionnelsDetail },
                        { cle: "les-deux", icone: "users-group-rounded-linear", titre: t.clientsDeux, detail: t.clientsDeuxDetail },
                    ].map((c) => (
                        <Choix
                            key={c.cle}
                            icone={c.icone}
                            titre={c.titre}
                            detail={c.detail}
                            onClick={() => {
                                setR((p) => ({ ...p, clients: c.cle }));
                                aller(c.cle === "professionnels" ? "horsSujet" : "secteur");
                            }}
                        />
                    ))}
                </div>
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">{t.prive}</p>
            </Ecran>
        );
    }

    if (etape === "secteur") {
        return (
            <Ecran key={etape} {...commun} phase={0} onRetour={retour} titre={t.secteurTitre} aide={t.secteurAide}>
                <div className="grid sm:grid-cols-2 gap-3">
                    {[
                        { cle: "sante", icone: "pulse-linear", titre: t.secteurSante },
                        { cle: "enseignementSuperieur", icone: "diploma-linear", titre: t.secteurEnseignement },
                        { cle: "serviceInteretGeneral", icone: "global-linear", titre: t.secteurInteretGeneral },
                        { cle: "autre", icone: "check-circle-linear", titre: t.secteurAutre },
                    ].map((c) => (
                        <Choix
                            key={c.cle}
                            icone={c.icone}
                            titre={c.titre}
                            onClick={() => {
                                setR((p) => ({ ...p, secteur: c.cle }));
                                aller(c.cle === "autre" ? "mediateur" : "horsSujet");
                            }}
                        />
                    ))}
                </div>
            </Ecran>
        );
    }

    if (etape === "horsSujet") {
        return (
            <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.horsSujetTitre} phases={t.phases} sens={sens} reduit={reduit}>
                <div className="mt-4">
                    <p className="text-[15px] leading-7 text-gray-600 dark:text-lightBlue mb-0">
                        {tri.motif === "entreProfessionnels" ? t.refusProfessionnels : t.refusSecteur}
                    </p>
                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            </Resultat>
        );
    }

    if (etape === "mediateur") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.mediateurTitre} aide={t.mediateurAide}>
                <div className="space-y-5">
                    <div>
                        <Segments
                            libelle={t.champMediateur}
                            options={[
                                { cle: "oui", libelle: t.mediateurOui },
                                { cle: "non", libelle: t.mediateurNon },
                                { cle: "jeNeSaisPas", libelle: t.mediateurSaisPas },
                            ]}
                            valeur={r.mediateur}
                            poser={poser("mediateur")}
                        />
                    </div>
                    {r.mediateur === "oui" && (
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Champ
                                libelle={t.champNomMediateur}
                                aide={t.aideNomMediateur}
                                valeur={r.nomMediateur}
                                poser={poser("nomMediateur")}
                            />
                            <Champ
                                libelle={t.champSiteMediateur}
                                valeur={r.siteMediateur}
                                poser={poser("siteMediateur")}
                            />
                        </div>
                    )}
                    <div>
                        <Segments
                            libelle={t.champForme}
                            options={[
                                { cle: "personnePhysique", libelle: t.formeIndividuelle },
                                { cle: "societe", libelle: t.formeSociete },
                            ]}
                            valeur={r.forme}
                            poser={poser("forme")}
                        />
                    </div>
                </div>
                {/* Sans médiateur, il n'y a rien à avoir écrit : on saute
                    l'écran des mentions plutôt que de poser une question dont
                    la réponse est déjà connue. */}
                <Avancer
                    pleineLargeur
                    disabled={!r.mediateur || !r.forme}
                    onClick={() => aller(r.mediateur === "oui" ? "mentions" : "resultat")}
                >
                    {r.mediateur === "oui" ? t.continuer : t.voirDiagnostic}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "mentions") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.mentionsTitre} aide={t.mentionsAide}>
                <div className="space-y-5">
                    <div>
                        <Segments libelle={t.champSite} options={ouiNon} valeur={r.site} poser={poser("site")} />
                    </div>
                    {r.site === "oui" && (
                        <div>
                            <Segments libelle={t.champMentionSite} options={ouiNon} valeur={r.mentionSite} poser={poser("mentionSite")} />
                        </div>
                    )}
                    <div>
                        <Segments
                            libelle={t.champMentionConditions}
                            options={ouiNon}
                            valeur={r.mentionConditions}
                            poser={poser("mentionConditions")}
                        />
                    </div>
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!r.site || !r.mentionConditions || (r.site === "oui" && !r.mentionSite)}
                    onClick={() => aller("resultat")}
                >
                    {t.voirDiagnostic}
                </Avancer>
            </Ecran>
        );
    }

    const libelleManque = {
        designer: t.manqueDesigner,
        ecrireEnsuite: t.manqueEcrireEnsuite,
        mentionSite: t.manqueMentionSite,
        mentionConditions: t.manqueMentionConditions,
    };

    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            <div className="mt-4">
                {etat.enRegle ? (
                    <div className="rounded-2xl border border-green-600/30 bg-green-600/[0.06] p-5 mb-6">
                        <div className="flex items-center gap-2.5 mb-2">
                            <SolarIcon
                                name="check-circle-linear"
                                className="text-xl shrink-0 text-green-700 dark:text-green-400"
                            />
                            <div className="font-bold dark:text-white leading-snug">{t.enRegleTitre}</div>
                        </div>
                        <p className="text-sm leading-7 text-gray-700 dark:text-lightBlue mb-0">
                            {t.enRegleTexte}
                        </p>
                    </div>
                ) : (
                    <div className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-5 mb-6">
                        <div className="flex items-center gap-2.5 mb-2">
                            <SolarIcon
                                name="danger-triangle-linear"
                                className="text-xl shrink-0 text-amber-600 dark:text-amber-400"
                            />
                            <div className="font-bold dark:text-white leading-snug">
                                {remplir(etat.manques.length > 1 ? t.manquesTitrePluriel : t.manquesTitre, {
                                    n: etat.manques.length,
                                })}
                            </div>
                        </div>
                        <p className="text-sm leading-7 text-gray-700 dark:text-lightBlue mb-0">
                            {remplir(t.manquesAide, { amende: euros(etat.amende, lang) })}
                        </p>
                        <Liste items={etat.manques.map((m) => libelleManque[m])} />
                    </div>
                )}

                <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                    {t.recapTitre}
                </div>
                <div className="rounded-2xl border border-gray-200/70 dark:border-white/10 overflow-hidden mb-6">
                    <table className="w-full text-sm">
                        <tbody>
                            {recap.map(([titre, valeur]) => (
                                <tr
                                    key={titre}
                                    className="border-b last:border-b-0 border-gray-200/70 dark:border-white/10"
                                >
                                    <td className="px-5 py-3 text-gray-500 dark:text-lightBlue/70 align-top w-3/5">
                                        {titre}
                                    </td>
                                    <td className="px-5 py-3 font-medium dark:text-white">{valeur}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {mention && (
                    <div className="mb-8">
                        <div className="flex items-center gap-2.5 mb-2">
                            <SolarIcon
                                name="pen-2-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-bold dark:text-white leading-snug">{t.mentionTitre}</div>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-lightBlue mb-3">{t.mentionAide}</p>
                        <div className="rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                            <p className="text-[15px] leading-7 text-gray-700 dark:text-lightBlue mb-0">
                                {mention}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={copier}
                            className="mt-3 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-gray-200/70 dark:border-white/10 text-gray-600 dark:text-lightBlue font-semibold transition-colors duration-200 hover:border-primary/40 hover:text-primary"
                        >
                            <SolarIcon name={copie ? "check-circle-linear" : "copy-linear"} className="text-lg" />
                            {copie ? t.copie : t.copier}
                        </button>
                    </div>
                )}

                <div className="mt-8">
                    <div className="flex items-center gap-2.5 mb-4">
                        <SolarIcon
                            name="clipboard-check-linear"
                            className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                        />
                        <div className="font-bold dark:text-white leading-snug">{t.suiteTitre}</div>
                    </div>
                    <ul className="space-y-3">
                        {[t.suite1, t.suite2, t.suite3, t.suite4].map((ligne) => (
                            <li
                                key={ligne}
                                className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue"
                            >
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300 dark:bg-white/20" />
                                <span>{ligne}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="mt-8 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                    <div className="flex items-center gap-2.5 mb-3">
                        <SolarIcon
                            name="shield-linear"
                            className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                        />
                        <div className="font-semibold dark:text-white leading-snug">{t.fondTitre}</div>
                    </div>
                    <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">
                        {remplir(t.fond, {
                            depuis: lang === "en" ? "1 January 2016" : "1er janvier 2016",
                            amendeIndividuelle: euros(AMENDE.personnePhysique, lang),
                            amendeSociete: euros(AMENDE.societe, lang),
                        })}
                    </p>
                </div>

                <Recommencer onClick={recommencer} libelle={t.recommencer} />
            </div>
        </Resultat>
    );
}
